import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { RIDEBOOK_KINDS, RIDEBOOK_KIND_LABELS, RIDEBOOK_SOURCE_LABELS, type Paginated, type RidebookEntry, type RidebookOverview, type RidebookVerifyResult } from '@kontor/shared';
import { api, queryString } from '../../api';
import { ExpandRow, Summary } from '../../components/ExpandRow';
import { FilterBar, type FilterField } from '../../components/filter/FilterBar';
import { useSession } from '../../session';
import { errorText, summary, when } from './shared';
import './ridebook.css';

type Tab = 'orders' | 'entries' | 'drafts';
const TABS: { key: Tab; label: string }[] = [
  { key: 'orders', label: 'Aufträge' },
  { key: 'entries', label: 'Alle Einträge' },
  { key: 'drafts', label: 'Entwürfe' },
];

/** Verweis auf den Auftrag oder Entwurf, zu dem ein Eintrag gehört */
export const entryLink = (e: RidebookEntry): string => (e.status === 'draft' ? `/auftragsbuch/entwurf/${e.id}` : `/auftragsbuch/${e.kind === 'received' ? e.id : (e.refId ?? e.id)}`);

/**
 * Auftragsbuch Personenbeförderung (Modul `ridebook`, § 49 Abs. 4 PBefG): die lückenlose, unveränderliche Kette der Aufträge. Oben stehen Stand und Prüfung der Kette, darunter die Aufträge, alle
 * Einträge oder die Entwürfe. Aufträge aus Wirkia stehen ohne Zutun im Buch, Einträge von Hand beginnen als Entwurf.
 */
export function RidebookPage() {
  const { can } = useSession();
  const [params, setParams] = useSearchParams();
  const tab = (params.get('tab') as Tab) || 'orders';
  const q = params.get('q') ?? '';
  const plate = params.get('plate') ?? '';
  const kind = params.get('kind') ?? '';
  const from = params.get('from') ?? '';
  const to = params.get('to') ?? '';
  const page = Number(params.get('page') ?? 1);
  const [verified, setVerified] = useState<RidebookVerifyResult | null>(null);

  const overview = useQuery({ queryKey: ['ridebook', 'overview'], queryFn: async () => (await api<{ data: RidebookOverview }>('GET', '/ridebook/overview')).data });
  const list = useQuery({
    queryKey: ['ridebook', 'list', { tab, q, plate, kind, from, to, page }],
    queryFn: () =>
      api<Paginated<RidebookEntry>>('GET', `/ridebook${queryString({ q, plate, kind: tab === 'entries' ? kind : '', from, to, page, perPage: 25, ...(tab === 'orders' ? { ordersOnly: 'true', status: 'final' } : tab === 'entries' ? { status: 'final' } : { status: 'draft' }) })}`),
    placeholderData: keepPreviousData,
  });
  const verify = useMutation({ mutationFn: async () => (await api<{ data: RidebookVerifyResult }>('GET', '/ridebook/verify')).data, onSuccess: setVerified });

  const update = (next: Record<string, string>) => {
    const merged = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) {
      if (v) merged.set(k, v);
      else merged.delete(k);
    }
    if (!('page' in next)) merged.delete('page');
    setParams(merged);
  };
  const fields: FilterField[] = [
    { key: 'plate', label: 'Kennzeichen', type: 'text' },
    ...(tab === 'entries' ? [{ key: 'kind', label: 'Art', type: 'select', options: [{ value: '', label: 'Alle' }, ...RIDEBOOK_KINDS.map((k) => ({ value: k, label: RIDEBOOK_KIND_LABELS[k] }))] } as FilterField] : []),
    { key: 'from', label: 'Von', type: 'date' },
    { key: 'to', label: 'Bis', type: 'date' },
  ];
  const o = overview.data;

  return (
    <>
      <div className="page-head">
        <h1>Auftragsbuch</h1>
        <span className="muted">{o ? `${o.total} Einträge im Buch` : ''}</span>
        {can('ridebook.write') && <Link className="btn btn--primary" to="/auftragsbuch/neu">Auftrag von Hand eintragen</Link>}
      </div>
      <p className="muted">
        Das Auftragsbuch nach § 49 Abs. 4 PBefG: Jeder Auftrag ist mit Eingang, Weitergabe an den Fahrer, Fahrtbeginn und Fahrtende lückenlos und unveränderlich erfasst. Aufträge, die Sie in Wirkia annehmen, kommen von selbst ins
        Buch. Was Sie von Hand eintragen, beginnt als Entwurf und gilt erst nach der Bestätigung.
      </p>

      <section className="card ridebook-state" aria-label="Stand des Auftragsbuchs">
        <div className="ridebook-state__grid">
          <div><span className="muted small">Einträge im Buch</span><strong>{o?.total ?? '–'}</strong></div>
          <div><span className="muted small">Letzte Nummer</span><strong>{o?.lastSeq ?? '–'}</strong></div>
          <div><span className="muted small">Entwürfe</span><strong>{o?.drafts ?? '–'}</strong>{o && o.incomplete > 0 && <small className="ridebook-warn">davon {o.incomplete} aus Wirkia mit fehlenden Angaben</small>}</div>
        </div>
        <div className="form-actions">
          <button type="button" className="btn btn--secondary" disabled={verify.isPending} onClick={() => verify.mutate()}>{verify.isPending ? 'Wird geprüft …' : 'Kette prüfen'}</button>
          <a className="btn btn--secondary" href={`/api/v1/ridebook/export${queryString({ from, to, plate, format: 'orders' })}`}>Auftragsbuch als CSV</a>
          <a className="btn btn--secondary" href={`/api/v1/ridebook/export${queryString({ from, to, plate, format: 'entries' })}`}>Einträge mit Hash als CSV</a>
        </div>
        {verify.isError && <div className="alert alert--error" role="alert">{errorText(verify.error, 'Die Prüfung hat nicht geklappt.')}</div>}
        {verified && (
          <div className={`alert ${verified.ok ? 'alert--success' : 'alert--error'}`} role={verified.ok ? 'status' : 'alert'}>
            <p>
              <strong>{verified.ok ? 'Die Kette ist in Ordnung.' : `Die Kette ist unterbrochen ab Nr. ${verified.brokenAt}.`}</strong> {verified.checked} Einträge geprüft{verified.sealChecked ? ', Siegel geprüft' : ''}, {when(verified.verifiedAt)}.
            </p>
            {verified.problems.length > 0 && <ul>{verified.problems.map((t) => <li key={t}>{t}</li>)}</ul>}
            {verified.headHash && <p className="small">Hash des letzten Eintrags: <code className="ridebook-hash">{verified.headHash}</code><br />Halten Sie ihn außerhalb von Wirkia fest (zum Beispiel beim Steuerberater), dann lässt sich später nachweisen, dass nichts ersetzt wurde.</p>}
          </div>
        )}
      </section>

      <div className="ridebook-tabs" role="tablist" aria-label="Ansicht">
        {TABS.map((t) => (
          <button key={t.key} type="button" role="tab" aria-selected={tab === t.key} className={`btn btn--sm ${tab === t.key ? 'btn--primary' : 'btn--secondary'}`} onClick={() => update({ tab: t.key === 'orders' ? '' : t.key, kind: '' })}>
            {t.label}{t.key === 'drafts' && o && o.drafts > 0 ? ` (${o.drafts})` : ''}
          </button>
        ))}
      </div>

      <FilterBar search={{ value: q, placeholder: 'Fahrgast, Ort oder Stichwort', onSubmit: (v) => update({ q: v }) }} fields={fields} values={{ plate, kind, from, to }} onChange={(key, value) => update({ [key]: value })} onReset={() => setParams(tab === 'orders' ? new URLSearchParams() : new URLSearchParams({ tab }))} />
      {list.isError && <div className="alert alert--error" role="alert">{(list.error as Error).message}</div>}

      <div className="card table-card">
        <table className="table table--cards">
          <thead>
            <tr><th>Nr.</th><th>Art</th><th>Zeitpunkt</th><th>Eintrag</th><th>Kennzeichen</th><th>Herkunft</th><th><span className="sr-only">Öffnen</span></th></tr>
          </thead>
          <tbody>
            {list.isLoading && <tr><td colSpan={7} className="muted">Wird geladen …</td></tr>}
            {list.data && list.data.data.length === 0 && <tr><td colSpan={7} className="empty">{tab === 'drafts' ? 'Keine Entwürfe.' : 'Noch nichts im Auftragsbuch.'}</td></tr>}
            {list.data?.data.map((e) => (
              <ExpandRow key={e.id} summary={<Summary title={summary(e)} aside={<strong>{e.seq ? `Nr. ${e.seq}` : 'Entwurf'}</strong>} line2={`${RIDEBOOK_KIND_LABELS[e.kind]} · ${when(e.occurredAt)}${e.plate ? ` · ${e.plate}` : ''}`} />}>
                <td className="cell-desktop-title">{e.seq ? <Link to={entryLink(e)}>{e.orderNo ? `Auftrag ${e.orderNo}` : `Nr. ${e.seq}`}</Link> : <Link to={entryLink(e)}>Entwurf</Link>}<div className="muted small">{e.seq ? `Eintrag ${e.seq}` : ''}</div></td>
                <td data-label="Art">{RIDEBOOK_KIND_LABELS[e.kind]}</td>
                <td data-label="Zeitpunkt">{when(e.occurredAt)}{e.lateMinutes !== null && <div className="ridebook-warn small">nachträglich erfasst ({when(e.finalizedAt)})</div>}</td>
                <td data-label="Eintrag">{summary(e)}{e.incomplete && <div className="ridebook-warn small">Es fehlen Angaben zur Fahrt</div>}</td>
                <td data-label="Kennzeichen">{e.plate ?? '–'}</td>
                <td data-label="Herkunft">{RIDEBOOK_SOURCE_LABELS[e.source]}{e.orderNumber && <div className="muted small">Auftrag {e.orderNumber}</div>}</td>
                <td className="cell-actions">
                  <Link className="btn btn--secondary btn--sm" to={entryLink(e)}>{e.status === 'draft' ? (can('ridebook.write') ? 'Entwurf bearbeiten' : 'Entwurf ansehen') : 'Details ansehen'}</Link>
                </td>
              </ExpandRow>
            ))}
          </tbody>
        </table>
      </div>
      {list.data && list.data.meta.lastPage > 1 && (
        <div className="pager">
          <button type="button" className="btn btn--secondary btn--sm" disabled={page <= 1} onClick={() => update({ page: String(page - 1) })}>Zurück</button>
          <span className="muted">Seite {page} von {list.data.meta.lastPage}</span>
          <button type="button" className="btn btn--secondary btn--sm" disabled={page >= list.data.meta.lastPage} onClick={() => update({ page: String(page + 1) })}>Weiter</button>
        </div>
      )}
    </>
  );
}
